import {
    CommunitySortOrder,
    EntityType,
    VehicleClassification,
} from '@polaris-customerweb/sharedcomponents.core';
import { isEqual } from 'lodash-es';
import { SearchFilter } from 'src/app/models/search-filter';
import { CommunityItemSearchFilterDto } from '../state/community/models/community-item-search-filter.dto';
import { DateFilterType } from './date-filter';
import { Pagination } from './pagination';

export class CommunityItemSearchFilter implements SearchFilter {
    id: string;
    dateFilterType?: DateFilterType;
    endDate?: Date;
    eventStartDate?: Date;
    eventEndDate?: Date;
    filterPastEvents?: boolean;
    includeFullDetail?: boolean;
    lat?: number;
    limit?: number;
    lng?: number;
    locationName: string;
    locationDescription: string;
    locationSet: boolean;
    maximumLength?: number;
    minimumLength?: number;
    minimumRating?: number;
    maximumRating?: number;
    pagination = new Pagination();
    radiusInMeters?: number;
    search: string;
    sort?: CommunitySortOrder;
    startDate?: Date;
    type?: EntityType[];
    vehicleTypes?: VehicleClassification[];

    refreshTimeInMilliseconds = 300000; // 5 minutes
    cacheDate?: Date;

    constructor(id: string) {
        this.id = id;
    }

    static fromSearchFilter(
        searchFilter: SearchFilter
    ): CommunityItemSearchFilter {
        return Object.assign(new CommunityItemSearchFilter(''), searchFilter);
    }

    static fromDto(
        dto: CommunityItemSearchFilterDto
    ): CommunityItemSearchFilter {
        return Object.assign(new CommunityItemSearchFilter(dto.id), dto, {
            pagination: Pagination.fromDto(dto.pagination),
        });
    }

    clone(): CommunityItemSearchFilter {
        const searchFilter = Object.assign(
            new CommunityItemSearchFilter(this.id),
            this
        );
        searchFilter.type = searchFilter.type
            ? [...searchFilter.type]
            : searchFilter.type;
        searchFilter.vehicleTypes = searchFilter.vehicleTypes
            ? [...searchFilter.vehicleTypes]
            : searchFilter.vehicleTypes;
        searchFilter.pagination = new Pagination(
            this.pagination.size,
            this.pagination.page
        );
        return searchFilter;
    }

    /** @description - Create a new filter with this filters configuration */
    cloneConfig(): CommunityItemSearchFilter {
        const searchFilter = new CommunityItemSearchFilter(this.id);
        searchFilter.type = this.type ? [...this.type] : this.type;
        searchFilter.refreshTimeInMilliseconds = this.refreshTimeInMilliseconds;
        searchFilter.filterPastEvents = this.filterPastEvents;

        if (this.pagination.isPaginated) {
            searchFilter.pagination.initialize();
            searchFilter.pagination.size = this.pagination.size;
        }

        return searchFilter;
    }

    hasFilterChanged(filter: CommunityItemSearchFilter): boolean {
        if (!filter) {
            return true;
        }

        return (
            this.endDate?.getTime() !== filter.endDate?.getTime() ||
            this.eventEndDate?.getTime() !== filter.eventEndDate?.getTime() ||
            this.eventStartDate?.getTime() !==
                filter.eventStartDate?.getTime() ||
            this.includeFullDetail !== filter.includeFullDetail ||
            this.lat !== filter.lat ||
            this.limit !== filter.limit ||
            this.lng !== filter.lng ||
            this.minimumLength !== filter.minimumLength ||
            this.maximumLength !== filter.maximumLength ||
            this.minimumRating !== filter.minimumRating ||
            this.maximumRating !== filter.maximumRating ||
            // do not compare page
            this.radiusInMeters !== filter.radiusInMeters ||
            this.search !== filter.search ||
            this.pagination.size !== filter.pagination.size ||
            this.sort !== filter.sort ||
            this.startDate?.getTime() !== filter.startDate?.getTime() ||
            !isEqual(this.type, filter.type) ||
            !isEqual(this.vehicleTypes, filter.vehicleTypes) ||
            this.isStale()
        );
    }

    hasPageChanged(filter: CommunityItemSearchFilter): boolean {
        return this.pagination.page === filter.pagination.page;
    }

    isStale(): boolean {
        return (
            !this.cacheDate ||
            Date.now() - this.cacheDate.getTime() >
                this.refreshTimeInMilliseconds
        );
    }

    toDto(): CommunityItemSearchFilterDto {
        const result = {} as CommunityItemSearchFilterDto;
        for (const key of Object.keys(this)) {
            if (typeof this[key] !== 'function') {
                result[key] = this[key];
            }
        }
        result.pagination = this.pagination.toDto();
        return result;
    }
}
